import { Chip } from '@/components/commons/chip'
import { Avatar } from '@/components/ui/avatar'
import { AlertTriangleIcon, CheckIcon, XCloseIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { categoryToLabel } from '@/features/experience-shell/utils.ts'
import { useAddExperienceExpertiseAnswerFlags } from '@/features/experience-vetting/queries/use-add-experience-expertise-answer-flags.ts'
import { useRemoveExperienceExpertiseAnswerFlags } from '@/features/experience-vetting/queries/use-remove-experience-expertise-answer-flags.ts'
import { useUpdateExperienceExpertiseStatus } from '@/features/experience-vetting/queries/use-update-experience-expertise-status.ts'
import type { RemapExperienceVetting } from '@/features/experience-vetting/types.ts'
import {
	OpsVettingLabel,
	OpsVettingParagraph,
	OpsVettingQuestionsGrid,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { VettingSectionStatusSelector } from '@/features/ops-vetting/components/vetting-section-status-selector'
import { useExperienceVettingError } from '@/features/service-vetting/store/use-service-vetting-error'
import { useTranslation } from '@/translations/i18n.tsx'
import { useAddExperienceExpertiseFlags } from '../queries/use-add-experience-expertise-flags'
import { useRemoveExperienceExpertiseFlags } from '../queries/use-remove-experience-expertise-flags'
import { ExpVettingCriteriaCategorySelect } from './exp-vetting-criteria-category-select'
import { ExperienceVettingCriteriaFlags } from './experience-vetting-criteria-flags'

export const ExperienceVettingCriteriaExpertise = ({
	experience,
	isReadOnly
}: {
	experience: RemapExperienceVetting
	isReadOnly: boolean
}) => {
	const { t } = useTranslation()
	const errors = useExperienceVettingError((state) => state.errors)

	const isBackToDraft = experience.expertise.status === 'back_to_draft'
	//render category label
	const renderCategoryLabel = (categoryKey?: string) => {
		if (!categoryKey || categoryKey === 'generic') {
			return '-'
		}
		return categoryToLabel[categoryKey as keyof typeof categoryToLabel]
			? t(categoryToLabel[categoryKey as keyof typeof categoryToLabel])
			: categoryKey
	}

	const addExpertiseQuestionFlags = useAddExperienceExpertiseAnswerFlags({
		experienceId: experience.id,
		sectionId: experience.expertise.id
	})
	const removeExpertiseQuestionFlags =
		useRemoveExperienceExpertiseAnswerFlags({
			experienceId: experience.id,
			sectionId: experience.expertise.id
		})

	const updateExpertiseStatus = useUpdateExperienceExpertiseStatus({
		experienceId: experience.id,
		expertiseId: experience.expertise.id
	})

	const addExpertiseFlags = useAddExperienceExpertiseFlags({
		experienceId: experience.id,
		itemId: experience.expertise.id
	})

	const removeExpertiseFlags = useRemoveExperienceExpertiseFlags({
		experienceId: experience.id,
		itemId: experience.expertise.id
	})

	const isDeclined = experience.expertise.status === 'declined'

	return (
		<OpsVettingSectionWrapper direction={'column'} fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<div className='flex shrink-0 items-center gap-8'>
					<OpsVettingSectionTitle>
						{t('vetting:criteria.service.expertise.title')}
					</OpsVettingSectionTitle>
					<VettingSectionStatusSelector
						disabled={isReadOnly}
						onChange={(status) =>
							updateExpertiseStatus.mutate({ status })
						}
						value={experience.expertise.status}
					/>
				</div>
				<div className='flex items-center gap-4'>
					{errors.expertiseErrors.map((error) => (
						<Chip
							isActive
							key={error}
							label={t(error)}
							startSlot={<AlertTriangleIcon />}
							variant={'danger'}
						/>
					))}
					{isDeclined && (
						<ExperienceVettingCriteriaFlags
							addFlag={(flag) =>
								addExpertiseFlags.mutate({
									flags: [flag]
								})
							}
							badgeVariant='danger'
							flagPosition='left'
							flags={experience.expertise.flags}
							hideAction={isReadOnly}
							removeFlag={(flag) =>
								removeExpertiseFlags.mutate({
									flags: [flag]
								})
							}
							scopes={['decline_expertise']}
						/>
					)}
				</div>
			</div>

			<Separator orientation='horizontal' />

			<OpsVettingQuestionsGrid>
				<div className='flex w-full flex-col gap-6'>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.category')}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{renderCategoryLabel(
								experience.category.category?.keyName
							)}
						</OpsVettingParagraph>
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.category_level_2')}
						</OpsVettingLabel>
						{experience.category.subCategory?.keyName &&
							experience.category.category?.keyName && (
								<ExpVettingCriteriaCategorySelect
									category={
										experience.category.category.keyName
									}
									defaultCategoryFormat={
										experience.category.subCategory.keyName
									}
									disabled={isReadOnly || isDeclined}
									experienceId={experience.id}
								/>
							)}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.experience.years_of_experience'
							)}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{
								experience.expertise.answers.expertise_years
									.answer
							}
						</OpsVettingParagraph>
						{/* {isBackToDraft && (
                            <ExperienceVettingCriteriaFlags
                                flags={experience.expertise.answers.expertise_years.flags}
                                addFlag={flag =>
                                    addExpertiseQuestionFlags.mutate({
                                        flags: [flag],
                                        itemId: experience.expertise.answers.expertise_years.id
                                    })
                                }
                                removeFlag={flag =>
                                    removeExpertiseQuestionFlags.mutate({
                                        flags: [flag],
                                        itemId: experience.expertise.answers.expertise_years.id
                                    })
                                }
                                scopes={['host_expertise']}
                                hideAction={isReadOnly}
                            />
                        )} */}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.listing_title')}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{
								experience.expertise.answers.experience_title
									.answer
							}
						</OpsVettingParagraph>
						{isBackToDraft && (
							<ExperienceVettingCriteriaFlags
								addFlag={(flag) =>
									addExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.experience_title.id
									})
								}
								flags={
									experience.expertise.answers
										.experience_title.flags
								}
								hideAction={isReadOnly}
								removeFlag={(flag) =>
									removeExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.experience_title.id
									})
								}
								scopes={['experience_title']}
							/>
						)}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.experience.listing_description'
							)}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{
								experience.expertise.answers
									.experience_description?.answer
							}
						</OpsVettingParagraph>
						{isBackToDraft && (
							<ExperienceVettingCriteriaFlags
								addFlag={(flag) =>
									addExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.experience_description.id
									})
								}
								flags={
									experience.expertise.answers
										.experience_description.flags
								}
								hideAction={isReadOnly}
								removeFlag={(flag) =>
									removeExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.experience_description.id
									})
								}
								scopes={['experience_description']}
							/>
						)}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.host_title')}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{
								experience.expertise.answers
									.host_experience_title?.answer
							}
						</OpsVettingParagraph>
						{isBackToDraft && (
							<ExperienceVettingCriteriaFlags
								addFlag={(flag) =>
									addExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.host_experience_title.id
									})
								}
								flags={
									experience.expertise.answers
										.host_experience_title.flags
								}
								hideAction={isReadOnly}
								removeFlag={(flag) =>
									removeExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.host_experience_title.id
									})
								}
								scopes={['host_title']}
							/>
						)}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.host_expertise')}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{
								experience.expertise.answers
									.host_expertise_description?.answer
							}
						</OpsVettingParagraph>
						{isBackToDraft && (
							<ExperienceVettingCriteriaFlags
								addFlag={(flag) =>
									addExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.host_expertise_description.id
									})
								}
								flags={
									experience.expertise.answers
										.host_expertise_description.flags
								}
								hideAction={isReadOnly}
								removeFlag={(flag) =>
									removeExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: experience.expertise.answers
											.host_expertise_description.id
									})
								}
								scopes={['host_expertise']}
							/>
						)}
					</div>
				</div>
				<Separator orientation='vertical' />
				<div className='flex w-full flex-col gap-6'>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.service.expertise.profile_picture'
							)}
						</OpsVettingLabel>
						<Avatar
							imageUrl={experience.host.profilePhotoUrl || ''}
							name={experience.host.firstName ?? '-'}
							size={'lg'}
						/>
						{isBackToDraft &&
							!!experience.expertise.answers?.profile_photo && (
								<ExperienceVettingCriteriaFlags
									addFlag={(flag) =>
										!!experience.expertise.answers
											?.profile_photo?.id &&
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: experience.expertise.answers
												.profile_photo.id
										})
									}
									flags={
										experience.expertise.answers
											.profile_photo.flags
									}
									hideAction={isReadOnly}
									removeFlag={(flag) =>
										!!experience.expertise.answers
											?.profile_photo?.id &&
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: experience.expertise.answers
												.profile_photo.id
										})
									}
									scopes={['profile_picture']}
								/>
							)}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.host_name')}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{experience.host.firstName}
						</OpsVettingParagraph>
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.experience.can_airbnb_import_content'
							)}
						</OpsVettingLabel>
						{experience.canAirbnbImportContent ? (
							<div className='flex items-center gap-2'>
								<CheckIcon size={16} />
								<OpsVettingParagraph>
									{t(
										'vetting:criteria.experience.can_airbnb_import_content_approved'
									)}
								</OpsVettingParagraph>
							</div>
						) : (
							<div className='flex items-center gap-2'>
								<XCloseIcon size={16} />
								<OpsVettingParagraph>
									{t(
										'vetting:criteria.experience.can_airbnb_import_content_not_approved'
									)}
								</OpsVettingParagraph>
							</div>
						)}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.meeting_point')}
						</OpsVettingLabel>
						<div className='flex flex-col gap-1'>
							<OpsVettingParagraph>
								<strong>City:</strong>{' '}
								{
									experience.expertise.answers.location_city
										?.answer
								}
							</OpsVettingParagraph>
							{!!experience.expertise.answers
								?.meeting_point_name && (
								<OpsVettingParagraph>
									<strong>Name:</strong>{' '}
									{
										experience.expertise.answers
											.meeting_point_name?.answer
									}
								</OpsVettingParagraph>
							)}
						</div>
						{isBackToDraft &&
							!!experience.expertise.answers
								?.meeting_point_name && (
								<ExperienceVettingCriteriaFlags
									addFlag={(flag) =>
										!!experience.expertise.answers
											?.meeting_point_name &&
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: experience.expertise.answers
												.meeting_point_name.id
										})
									}
									flags={
										experience.expertise.answers
											.meeting_point_name.flags
									}
									hideAction={isReadOnly}
									removeFlag={(flag) =>
										!!experience.expertise.answers
											?.meeting_point_name &&
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: experience.expertise.answers
												.meeting_point_name.id
										})
									}
									scopes={['location']}
								/>
							)}
					</div>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t('vetting:criteria.experience.price_per_person')}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{
								experience.expertise.answers
									.guest_individual_price?.answer
							}
						</OpsVettingParagraph>
						{/* {isBackToDraft && (
                            <ExperienceVettingCriteriaFlags
                                flags={experience.expertise.answers.guest_individual_price.flags}
                                addFlag={flag =>
                                    addExpertiseQuestionFlags.mutate({
                                        flags: [flag],
                                        itemId: experience.expertise.answers.guest_individual_price.id
                                    })
                                }
                                removeFlag={flag =>
                                    removeExpertiseQuestionFlags.mutate({
                                        flags: [flag],
                                        itemId: experience.expertise.answers.guest_individual_price.id
                                    })
                                }
                                scopes={['host_expertise']}
                                hideAction={isReadOnly}
                            />
                        )} */}
					</div>
				</div>
			</OpsVettingQuestionsGrid>
		</OpsVettingSectionWrapper>
	)
}
